import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { SearchInput } from "@/components/SearchInput";
import {
    normalizeNavigationSearch as normalize,
    type NavigationArea,
} from "@/components/workspace/navigation";

interface NavigationCommandProps {
    areas: NavigationArea[];
    open: boolean;
    onClose: () => void;
}

export const NavigationCommand = ({
    areas,
    open,
    onClose,
}: NavigationCommandProps) => {
    const { t } = useTranslation("global");
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(0);
    const [category, setCategory] = useState<
        "menus" | "settings" | "all"
    >("menus");
    const input = useRef<HTMLInputElement>(null);
    const menuResults = areas
        .flatMap((area, areaIndex) =>
            area.items.map((item) => ({
                ...item,
                area: area.label,
                areaIndex,
            }))
        )
        .filter((item) =>
            normalize(`${item.label} ${item.area}`).includes(normalize(search))
        );
    const settingsResults = [
        {
            id: "settings-account",
            path: "/settings/account",
            label: t("settings.tabs.account"),
            icon: "user",
            area: t("settings.title"),
            areaIndex: 0,
        },
        {
            id: "settings-appearance",
            path: "/settings/appearance",
            label: t("settings.tabs.appearance"),
            icon: "sun",
            area: t("settings.title"),
            areaIndex: 0,
        },
        {
            id: "settings-security-password",
            path: "/settings/security/password",
            label: t("settings.security.tabs.password"),
            icon: "key",
            area: t("settings.tabs.security"),
            areaIndex: 1,
        },
        {
            id: "settings-security-2fa",
            path: "/settings/security/2fa",
            label: t("settings.security.tabs.two_factor"),
            icon: "shield",
            area: t("settings.tabs.security"),
            areaIndex: 1,
        },
        {
            id: "settings-security-biometrics",
            path: "/settings/security/biometrics",
            label: t("settings.security.tabs.biometrics"),
            icon: "fingerprint",
            area: t("settings.tabs.security"),
            areaIndex: 1,
        },
    ].filter((item) =>
        normalize(`${item.label} ${item.area}`).includes(normalize(search))
    );
    const results =
        category === "menus"
            ? menuResults
            : category === "settings"
              ? settingsResults
              : [...menuResults, ...settingsResults];
    useEffect(() => {
        if (!open) {
            return;
        }
        setSearch("");
        setSelected(0);
        const timeout = window.setTimeout(() => input.current?.focus(), 100);
        return () => window.clearTimeout(timeout);
    }, [open]);
    useEffect(() => {
        if (!open) {
            return;
        }

        const handleEscape = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);

        return () => document.removeEventListener("keydown", handleEscape);
    }, [open, onClose]);
    useEffect(() => {
        document
            .getElementById(`navigation-result-${selected}`)
            ?.scrollIntoView({ block: "nearest" });
    }, [selected]);
    const go = (path?: string): void => {
        if (path) {
            onClose();
            navigate(path);
        }
    };
    if (!open) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-950/15 px-4 pt-[18vh] backdrop-blur-md animate-fade-in"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="w-full max-w-2xl overflow-hidden rounded-2xl border border-white/50 bg-white/55 shadow-[0_24px_80px_rgba(15,23,42,0.22)] backdrop-blur-2xl ring-0 dark:border-white/10 dark:bg-zinc-900/55"
                role="dialog"
                aria-modal="true"
                aria-label={t("workspace.search_title")}
            >
                <div className="flex items-center gap-3 px-4 py-2">
                    <SearchInput
                        ref={input}
                        value={search}
                        onChange={(value) => {
                            setSearch(value);
                            setSelected(0);
                        }}
                        aria-label={t("workspace.search_title")}
                        placeholder={t("workspace.search_placeholder")}
                        wrapperClassName="flex-1"
                        className="!border-0 !bg-white/90 py-3 text-base !shadow-none !outline-none !ring-0 focus:!border-0 focus:!shadow-none focus:!ring-0 dark:!bg-zinc-800/90"
                        role="combobox"
                        aria-expanded
                        aria-controls="navigation-results"
                        aria-autocomplete="list"
                        aria-activedescendant={
                            results.length
                                ? `navigation-result-${selected}`
                                : undefined
                        }
                        onKeyDown={(event) => {
                            if (
                                event.key === "ArrowDown" ||
                                event.key === "ArrowUp"
                            ) {
                                event.preventDefault();
                                setSelected((previous) =>
                                    results.length
                                        ? (previous +
                                              (event.key === "ArrowDown"
                                                  ? 1
                                                  : -1) +
                                              results.length) %
                                          results.length
                                        : 0
                                );
                            }
                            if (event.key === "Enter") {
                                event.preventDefault();
                                go(results[selected]?.path);
                            }
                        }}
                    />
                    <div className="hidden shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-500 sm:flex dark:border-white/10 dark:bg-white/5 dark:text-zinc-400">
                        <div>ESC</div>
                    </div>
                </div>
                <div className="flex items-center justify-center gap-2 px-4 py-1">
                    <button
                        type="button"
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${category === "menus" ? "bg-primary text-white shadow-sm" : "text-slate-500 hover:bg-white/60 hover:text-primary dark:text-zinc-400 dark:hover:bg-white/10"}`}
                        onClick={() => {
                            setCategory("menus");
                            setSelected(0);
                        }}
                    >
                        {t("workspace.screens")}
                    </button>
                    <button
                        type="button"
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${category === "settings" ? "bg-primary text-white shadow-sm" : "text-slate-500 hover:bg-white/60 hover:text-primary dark:text-zinc-400 dark:hover:bg-white/10"}`}
                        onClick={() => {
                            setCategory("settings");
                            setSelected(0);
                        }}
                    >
                        {t("settings.title")}
                    </button>
                    <button
                        type="button"
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${category === "all" ? "bg-primary text-white shadow-sm" : "text-slate-500 hover:bg-white/60 hover:text-primary dark:text-zinc-400 dark:hover:bg-white/10"}`}
                        onClick={() => {
                            setCategory("all");
                            setSelected(0);
                        }}
                    >
                        {t("workspace.all")}
                    </button>
                </div>
                <div
                id="navigation-results"
                role="listbox"
                aria-label={t("workspace.screens")}
                    className="max-h-[min(52vh,26rem)] space-y-1 overflow-y-auto p-3"
                >
                {results.map((item, index) => (
                    <div key={item.id}>
                        {(index === 0 || results[index - 1].areaIndex !== item.areaIndex) && (
                            <div className="px-3 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 first:pt-0 dark:text-zinc-500">
                                {item.area}
                            </div>
                        )}
                        <div
                            id={`navigation-result-${index}`}
                            role="option"
                            aria-selected={selected === index}
                        >
                            <Button
                                variant="ghost"
                                icon={
                                    <Icon
                                        value={item.icon}
                                        size={18}
                                        className="shrink-0 text-slate-500"
                                    />
                                }
                                onClick={() => go(item.path)}
                                tabIndex={-1}
                                contentClassName="flex-1 !justify-between"
                                className={`!w-full !justify-start !gap-3 !rounded-lg !p-3 ${selected === index ? "!bg-primary/10" : ""}`}
                            >
                                <div className="truncate">{item.label}</div>
                            </Button>
                        </div>
                    </div>
                ))}
                {!results.length && (
                    <div className="py-8 text-center text-sm text-slate-500">
                        {t("workspace.search_empty")}
                    </div>
                )}
                </div>
                <div className="hidden items-center justify-end border-t border-slate-200/60 px-5 py-3 text-[11px] text-slate-500 sm:flex dark:border-white/10 dark:text-zinc-400">
                    <div className="flex items-center gap-3">
                        <div><strong className="font-semibold text-slate-700 dark:text-zinc-200">↑↓</strong> navegar</div>
                        <div><strong className="font-semibold text-slate-700 dark:text-zinc-200">↵</strong> abrir</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
